import React, { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, ChevronLeft, ChevronRight, X, Timer } from "lucide-react";
import DahliasShell from "../components/dahlias/DahliasShell";
import { DahliasLogo, HeritageSealSilhouette } from "../components/brand/Logos";
import CmsText from "../components/CmsText";
import { useSiteText, resolveAsset } from "../store/useSiteContent";
import { HERITAGE_ASSETS } from "../components/dahlias/heritageAssets";
import useDocumentTitle from "../hooks/useDocumentTitle";
import { useSiteSettings } from "../hooks/siteSettings";

/* -------------------------------------------------------------------------- */
/*  Heritage Dahlias, "Heirloom flower house" home page                      */
/*  Built around stained-glass farm illustrations, jewel colours,             */
/*  the Heritage Seal as a signature, and the farm as the hero.               */
/* -------------------------------------------------------------------------- */

const SEASONS = [
  {
    label: "Spring",
    months: "Sept to Nov",
    note: "Tubers wake, eyes emerge, beds are prepared.",
    img: "/photos/brand/seasons/spring.webp",
  },
  {
    label: "Summer",
    months: "Dec to Feb",
    note: "The fields bloom. Baskets fill at first light.",
    img: "/photos/brand/seasons/summer.webp",
  },
  {
    label: "Autumn",
    months: "Mar to May",
    note: "The last great flush. Bouquets and farewells.",
    img: "/photos/brand/seasons/autumn.webp",
  },
  {
    label: "Winter",
    months: "Jun to Aug",
    note: "Tubers rest in the shed. Catalogues are written.",
    img: "/photos/brand/seasons/winter.webp",
  },
];

const TIMELINE = [
  { year: "1932", title: "Nana Jean is born", note: "The story begins on a small Aotearoa farm." },
  { year: "1950s", title: "First dahlia bed", note: "A handful of tubers, planted in a backyard row." },
  { year: "1980s", title: "The Mum's Shed", note: "A little red shed becomes the heart of the farm." },
  { year: "2020s", title: "Heritage Dahlias", note: "Her grandchildren grow the legacy on, by hand." },
  { year: "Today", title: "Carrying on", note: "Tubers shared a few hundred at a time." },
];

export const DEFAULT_ANNOUNCEMENTS = [
  {
    id: "tubers-now",
    kicker: "From the shed",
    heading: "Tubers",
    heading_italic: "available now.",
    body: "Heirloom dahlia tubers, divided by hand in the Mum's Shed and ready to plant. First in, best dressed.",
    cta_label: "Shop the store",
    cta_link: "/dahlias/store",
    image: "",
    countdown_date: "2026-09-01",
    countdown_label: "Tuber sales open in",
    enabled: true,
  },
];

export const DEFAULT_GALLERY = [
  { id: "field", image: "/photos/dahlia-field.jpg", caption: "The dahlia field in full summer flush." },
  { id: "cut", image: "/photos/cut-dahlias-spread.jpg", caption: "Fresh cut blooms, picked at first light." },
  { id: "pink", image: "/photos/open-pink-dahlias.jpg", caption: "Heirloom varieties, grown the slow way." },
  { id: "shed", image: "/photos/mums-shed.jpg", caption: "The Mum's Shed, the heart of the farm." },
];

/* ----------------------------------------------------------- HERO --------- */

const HERO_BANNER_DEFAULT = "https://customer-assets-0z36b82j.emergentagent.net/job_apparel-dahlias/artifacts/ow2cjqay_HERITAGE-FLATLAY-ADMIN.png";
const HERO_LOGO_DEFAULT = "https://customer-assets-0z36b82j.emergentagent.net/job_apparel-dahlias/artifacts/v0zh9dkq_file_00000000fe1c7207a9134d62783434dc%281%29.png";

function HeritageHero({ image, subtitle, logo }) {
  return (
    <section
      data-testid="dahlias-hero"
      className="relative overflow-hidden bg-[#1F2D1B] text-[#FBE6D8]"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url(${image || HERO_BANNER_DEFAULT})`,
          backgroundPosition: "center 25%",
        }}
      />
      {/* Cream → plum vignette so the headline reads */}
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(31,45,27,0.10) 0%, rgba(31,45,27,0.15) 45%, rgba(31,45,27,0.80) 100%)",
        }}
      />
      {/* Soft warm ring around the seal */}
      <div className="relative max-w-7xl mx-auto px-5 md:px-10 pt-20 md:pt-32 pb-24 md:pb-40 text-center">
        <span className="font-label tracking-[0.5em] text-[11px] md:text-xs text-[#FBE6D8]/90 uppercase">
          ✦ Est · 1932 · Aotearoa ✦
        </span>

        <h1
          data-testid="dahlias-hero-headline"
          className="mt-6 font-display uppercase tracking-[0.12em] text-[clamp(1.5rem,8.5vw,4.5rem)] md:text-6xl lg:text-7xl text-[#FBE6D8] leading-[1.05] drop-shadow-[0_4px_18px_rgba(20,10,5,0.55)] whitespace-nowrap"
        >
          HERITAGE DAHLIAS
        </h1>

        <p className="mt-7 max-w-xl mx-auto text-lg md:text-2xl font-display-italic text-[#FBE6D8]/95 leading-relaxed drop-shadow-[0_2px_10px_rgba(20,10,5,0.55)]">
          {subtitle || "Growing exceptional dahlias since 1932."}
        </p>

        {/* Simple heritage outline logo, big, floating on the scene */}
        <div className="mt-10 flex items-center justify-center" data-testid="hero-logo">
          <img
            src={logo || HERO_LOGO_DEFAULT}
            alt="Heritage Dahlias"
            className="w-40 h-40 md:w-60 md:h-60 object-contain rounded-full drop-shadow-[0_6px_24px_rgba(20,10,5,0.6)]"
          />
        </div>

        <div className="mt-12 flex flex-wrap items-center justify-center gap-4 md:gap-5">
          <Link
            to="/dahlias/store"
            data-testid="hero-shop-cta"
            className="inline-flex items-center gap-3 bg-[#6E1E3A] hover:bg-[#8a2548] text-[#FBE6D8] px-9 md:px-11 py-4 md:py-5 wobble-pill font-label tracking-[0.3em] text-xs md:text-sm uppercase shadow-2xl border-2 border-[#FBE6D8]/15 transition-all hover:-translate-y-0.5"
          >
            Shop Dahlias <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/dahlias/about"
            data-testid="hero-story-cta"
            className="inline-flex items-center gap-3 bg-transparent text-[#FBE6D8] border-2 border-[#FBE6D8]/65 hover:bg-[#FBE6D8]/15 px-9 md:px-11 py-4 md:py-5 wobble-pill font-label tracking-[0.3em] text-xs md:text-sm uppercase transition-all hover:-translate-y-0.5"
          >
            Our Story <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      {/* Scalloped bottom transition into cream, cream fingers reach up
          into the dark hero so the wave looks like a botanical ribbon. */}
      <div aria-hidden="true" className="absolute -bottom-px left-0 right-0 h-7 bg-[var(--dahlias-cream)] scallop-bottom" />
    </section>
  );
}

/* ------------------------------------------------------- INTRODUCTION ----- */

function Introduction({ heading, body }) {
  return (
    <section
      data-testid="dahlias-intro"
      className="bg-[var(--dahlias-cream)] text-[var(--dahlias-ink)] px-5 md:px-10"
    >
      <div className="max-w-3xl mx-auto py-20 md:py-28 text-center">
        <span className="font-label tracking-[0.5em] text-[11px] text-[#6E1E3A] uppercase">
          ✦ A Heritage Flower Farm ✦
        </span>
        <h2 className="mt-5 font-display text-4xl md:text-5xl text-[var(--dahlias-forest)] leading-[1.05] whitespace-pre-line">
          <CmsText text={heading} italicClass="font-display-italic text-[#6E1E3A]" />
        </h2>
        <p className="mt-8 text-lg md:text-xl text-[var(--dahlias-ink)]/80 leading-relaxed whitespace-pre-line">
          {body}
        </p>
        <div className="mt-10 flex items-center justify-center gap-4 text-[#6E1E3A]/70">
          <span className="h-px w-12 md:w-20 bg-current" />
          <Sparkles className="w-4 h-4" />
          <span className="h-px w-12 md:w-20 bg-current" />
        </div>
      </div>
    </section>
  );
}

/* ----------------------------------------------------- LIFE ON THE FARM --- */

function LifeOnTheFarm() {
  return (
    <section
      data-testid="dahlias-farm-life"
      className="bg-[var(--dahlias-cream)] text-[var(--dahlias-ink)]"
    >
      <div className="max-w-3xl mx-auto px-5 md:px-10 py-20 md:py-28 text-center">
        <span className="font-label tracking-[0.5em] text-[11px] text-[#6E1E3A] uppercase">
          ✦ Life on the Farm ✦
        </span>
        <h2 className="mt-4 font-display text-4xl md:text-5xl text-[var(--dahlias-forest)] leading-[1.05]">
          Planted, tended,{" "}
          <span className="font-display-italic text-[#6E1E3A]">picked by hand.</span>
        </h2>
        <p className="mt-6 text-lg md:text-xl text-[var(--dahlias-ink)]/80 leading-relaxed">
          From the first eye breaking on a tuber in the shed, to a basket of blooms walking back through the garden gate. The seasons here are slow, deliberate and full of life.
        </p>
        <ul className="mt-8 inline-grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-3 text-base md:text-lg text-[var(--dahlias-ink)]/85 text-left">
          {[
            "Planting in spring rows",
            "Growing through the long summer",
            "Cutting baskets at first light",
            "Dividing tubers in the shed",
            "Packing orders by candlelight",
          ].map((line) => (
            <li key={line} className="flex items-center gap-3">
              <span
                aria-hidden="true"
                className="w-2.5 h-2.5 rounded-full bg-[#6E1E3A] shrink-0"
              />
              <span className="font-display-italic">{line}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* -------------------------------------------------- HERITAGE SINCE 1932 --- */

function HeritageTimeline({ items }) {
  const entries = items && items.length ? items : TIMELINE;
  return (
    <section
      data-testid="dahlias-timeline"
      className="relative overflow-hidden text-[#FBE6D8] border-y-[3px] border-[#6E1E3A]/40 bg-[#1F2D1B]"
    >
      {/* Stained-glass flat-lay backdrop, the same seasonal artwork that
          appears in the catalogue, tiled 2×2 so the timeline feels like it
          sits inside the farm's seasonal cycle. */}
      <div
        aria-hidden="true"
        className="absolute inset-0 grid grid-cols-2 grid-rows-2"
      >
        <img src="/photos/brand/seasons/spring.webp" alt="" className="w-full h-full object-cover" loading="lazy" />
        <img src="/photos/brand/seasons/summer.webp" alt="" className="w-full h-full object-cover" loading="lazy" />
        <img src="/photos/brand/seasons/autumn.webp" alt="" className="w-full h-full object-cover" loading="lazy" />
        <img src="/photos/brand/seasons/winter.webp" alt="" className="w-full h-full object-cover" loading="lazy" />
      </div>
      {/* Plum + forest overlay so timeline text stays crisp on top of the
          stained-glass panels. */}
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(31,45,27,0.72) 0%, rgba(110,30,58,0.78) 100%)",
        }}
      />

      <div className="relative max-w-6xl mx-auto px-5 md:px-10 py-20 md:py-28">
        <div className="text-center mb-14 md:mb-16">
          <span className="font-label tracking-[0.5em] text-[11px] text-[#F4C8D2] uppercase drop-shadow-[0_2px_8px_rgba(20,10,5,0.7)]">
            ✦ A Family Archive ✦
          </span>
          <h2 className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl text-[#FBE6D8] leading-[1.05] drop-shadow-[0_3px_14px_rgba(20,10,5,0.7)]">
            Heritage since{" "}
            <span className="font-display-italic text-[#F4C8D2]">1932.</span>
          </h2>
        </div>

        <ol className="relative max-w-3xl mx-auto">
          <span
            aria-hidden="true"
            className="absolute left-[18px] md:left-1/2 top-0 bottom-0 w-px bg-[#FBE6D8]/40"
          />
          {entries.map((t, i) => (
            <li
              key={`${t.year}-${i}`}
              className={`relative pl-14 md:pl-0 md:grid md:grid-cols-2 md:gap-12 md:items-start pb-12 md:pb-14 last:pb-0 ${
                i % 2 === 0 ? "md:text-right" : "md:flex-row-reverse md:[&>*:first-child]:order-2"
              }`}
            >
              {/* seal marker */}
              <span
                aria-hidden="true"
                className="absolute left-0 md:left-1/2 top-0 -translate-x-1/2 md:-translate-x-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-[#FBE6D8] border-2 border-[#6E1E3A] flex items-center justify-center shadow-lg"
              >
                <HeritageSealSilhouette color="#6E1E3A" className="w-6 h-6 md:w-7 md:h-7" />
              </span>
              <div className={i % 2 === 0 ? "md:pr-12" : "md:pl-12 md:col-start-2"}>
                <div className="font-display text-3xl md:text-4xl text-[#F4C8D2] drop-shadow-[0_2px_8px_rgba(20,10,5,0.7)]">{t.year}</div>
                <div className="mt-1 font-display-italic text-xl md:text-2xl text-[#FBE6D8] drop-shadow-[0_2px_8px_rgba(20,10,5,0.7)]">
                  {t.title}
                </div>
                <p className="mt-2 text-[#FBE6D8]/90 leading-relaxed drop-shadow-[0_2px_8px_rgba(20,10,5,0.7)]">{t.note}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

/* ---------------------------------------------------- SEASONAL HIGHLIGHTS --*/

function Seasons() {
  return (
    <section
      data-testid="dahlias-seasons"
      className="bg-[var(--dahlias-cream)] text-[var(--dahlias-ink)]"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-28">
        <div className="text-center mb-14 md:mb-16">
          <span className="font-label tracking-[0.5em] text-[11px] text-[#6E1E3A] uppercase">
            ✦ Through the Year ✦
          </span>
          <h2 className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl text-[var(--dahlias-forest)] leading-[1.05]">
            Seasonal{" "}
            <span className="font-display-italic text-[#6E1E3A]">Highlights.</span>
          </h2>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {SEASONS.map((s) => (
            <div
              key={s.label}
              data-testid={`season-${s.label.toLowerCase()}`}
              className="group relative wobble-a overflow-hidden border border-[#6E1E3A]/20 bg-[var(--dahlias-paper)] transition-transform duration-500 hover:-translate-y-1 hover:shadow-2xl"
            >
              {/* Stained-glass seasonal artwork, supplied by Heritage */}
              <div className="relative aspect-square bg-[#1F2D1B]">
                <img
                  src={s.img}
                  alt={`${s.label}, Heritage Dahlias`}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div
                  aria-hidden="true"
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      "linear-gradient(180deg, transparent 40%, rgba(20,10,5,0.55) 100%)",
                  }}
                />
                <div className="absolute inset-0 flex flex-col justify-end p-5 md:p-6 text-[#FBE6D8]">
                  <span className="font-label tracking-[0.4em] text-[10px] uppercase text-[#FBE6D8]/90 drop-shadow-[0_2px_6px_rgba(20,10,5,0.6)]">
                    {s.months}
                  </span>
                  <h3 className="mt-1 font-display text-3xl md:text-4xl drop-shadow-[0_2px_8px_rgba(20,10,5,0.7)]">
                    {s.label}
                  </h3>
                </div>
              </div>
              <p className="px-5 md:px-6 py-5 text-sm md:text-base text-[var(--dahlias-ink)]/80 leading-relaxed">
                {s.note}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------- ANNOUNCEMENTS (CMS-driven) --- */

function AnnouncementCta({ link, label }) {
  const cls = "mt-7 inline-flex items-center gap-3 bg-[#6E1E3A] hover:bg-[#5A1830] text-[#F4ECD8] px-9 py-4 font-display tracking-[0.2em] text-xs uppercase rounded-full shadow-[0_6px_24px_rgba(110,30,58,0.32)] transition-all hover:-translate-y-0.5";
  if (!label) return null;
  if (/^https?:\/\//i.test(link || "")) {
    return <a href={link} className={cls} data-testid="announcement-cta">{label} <ArrowRight className="w-4 h-4" /></a>;
  }
  return <Link to={link || "/dahlias/store"} className={cls} data-testid="announcement-cta">{label} <ArrowRight className="w-4 h-4" /></Link>;
}

function AnnouncementCountdown({ date, label }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(t);
  }, []);
  if (!date) return null;
  const target = new Date(`${date}T00:00:00+12:00`).getTime();
  const diff = target - now;
  if (Number.isNaN(target) || diff <= 0) return null;
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const big = days > 0 ? `${days} day${days === 1 ? "" : "s"}` : `${hours} hour${hours === 1 ? "" : "s"}`;
  return (
    <div
      data-testid="announcement-countdown"
      className="mt-5 inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full border-2 border-[#6E1E3A]/30 bg-white/60 text-[#6E1E3A]"
    >
      <Timer className="w-4 h-4" />
      <span className="font-label tracking-[0.25em] text-[10px] md:text-[11px] uppercase">
        {label || "Opens in"}
      </span>
      <span className="font-display text-lg md:text-xl leading-none">{big}</span>
      {days > 0 && hours > 0 && (
        <span className="font-display-italic text-sm opacity-70">{hours} hrs</span>
      )}
    </div>
  );
}

function AnnouncementPanel({ a, index }) {
  return (
    <section
      data-testid={`dahlias-announcement-${a.id || index}`}
      className="relative overflow-hidden border-t-2 border-[#6E1E3A]/20"
      style={{ backgroundColor: "#F4ECD8" }}
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(ellipse at 80% 20%, rgba(255,255,255,0.45) 0%, transparent 55%), url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='220' height='220'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' seed='7'/><feColorMatrix values='0 0 0 0 0.43  0 0 0 0 0.12  0 0 0 0 0.23  0 0 0 0.10 0'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.55'/></svg>\")",
          backgroundSize: "auto, 240px 240px",
        }}
      />
      <div className="relative max-w-5xl mx-auto px-5 md:px-10 py-16 md:py-20 grid md:grid-cols-[1fr_auto] gap-8 md:gap-12 items-center">
        <div className="text-center md:text-left">
          {a.kicker && (
            <div className="font-label tracking-[0.45em] text-[10px] md:text-[11px] text-[#6E1E3A] uppercase inline-flex items-center gap-2">
              <Sparkles className="w-3 h-3" /> {a.kicker}
            </div>
          )}
          <h2 className="mt-2 font-display uppercase text-4xl md:text-6xl text-[#3C2A1E] leading-[0.95] tracking-tight">
            {a.heading}
            {a.heading_italic && (
              <>
                <br />
                <span className="font-display-italic normal-case text-[#6E1E3A]">{a.heading_italic}</span>
              </>
            )}
          </h2>
          {a.body && (
            <p className="mt-5 text-base md:text-lg text-[#3C2A1E]/85 font-display italic max-w-xl leading-relaxed whitespace-pre-line">
              {a.body}
            </p>
          )}
          <AnnouncementCountdown date={a.countdown_date} label={a.countdown_label} />
          <div>
            <AnnouncementCta link={a.cta_link} label={a.cta_label} />
          </div>
        </div>
        {a.image && (
          <div className="hidden md:block shrink-0 -rotate-2">
            <img
              src={resolveAsset(a.image, "")}
              alt={a.heading || "Heritage announcement"}
              loading="lazy"
              className="w-44 h-44 lg:w-56 lg:h-56 object-cover rounded-sm shadow-[0_8px_22px_rgba(60,42,30,0.18)] border-2 border-[#F4ECD8]"
            />
          </div>
        )}
      </div>
    </section>
  );
}

function HomeAnnouncements({ items }) {
  const visible = (items && items.length ? items : DEFAULT_ANNOUNCEMENTS).filter((a) => a.enabled !== false && (a.heading || a.body));
  if (!visible.length) return null;
  return <>{visible.map((a, i) => <AnnouncementPanel key={a.id || i} a={a} index={i} />)}</>;
}

/* --------------------------------------------- ROTATING GALLERY (CMS) ---- */

function GalleryRow({ photos, row, reverse = false, frozen = false, onOpen }) {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused || frozen || photos.length < 2) return undefined;
    const t = setInterval(
      () => setIdx((i) => (i + (reverse ? -1 : 1) + photos.length) % photos.length),
      reverse ? 6200 : 5000,
    );
    return () => clearInterval(t);
  }, [paused, frozen, photos.length, reverse]);

  if (!photos.length) return null;
  const suffix = row === 1 ? "" : `-${row}`;
  const go = (dir) => setIdx((i) => (i + dir + photos.length) % photos.length);

  return (
    <div
      className={`relative overflow-hidden border-2 border-[#6E1E3A]/20 bg-[#1F2D1B] shadow-2xl ${row === 1 ? "wobble-a" : "wobble-b"}`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid={`gallery-row-${row}`}
    >
      <div className="relative aspect-[4/3] sm:aspect-[16/9] lg:aspect-[24/9]">
        {photos.map((p, i) => (
          <div
            key={p.id || i}
            aria-hidden={i !== idx}
            className="absolute inset-0 transition-opacity duration-700"
            style={{ opacity: i === idx ? 1 : 0, pointerEvents: i === idx ? "auto" : "none" }}
          >
            <img
              src={resolveAsset(p.image, "")}
              alt={p.caption || `Heritage Dahlias photo`}
              loading={i === 0 && row === 1 ? "eager" : "lazy"}
              onClick={() => onOpen(p)}
              data-testid={`gallery-slide${suffix}-${i}`}
              className="absolute inset-0 w-full h-full object-cover cursor-zoom-in"
            />
            {p.caption && (
              <>
                <div
                  aria-hidden="true"
                  className="absolute inset-x-0 bottom-0 h-32 pointer-events-none"
                  style={{ background: "linear-gradient(180deg, transparent, rgba(20,10,5,0.65))" }}
                />
                <p className="absolute bottom-4 md:bottom-6 left-5 md:left-8 right-16 font-display-italic text-lg md:text-2xl text-[#FBE6D8] drop-shadow-[0_2px_8px_rgba(20,10,5,0.7)] pointer-events-none">
                  {p.caption}
                </p>
              </>
            )}
          </div>
        ))}

        {photos.length > 1 && (
          <>
            <button
              type="button"
              onClick={() => go(-1)}
              aria-label="Previous photo"
              data-testid={`gallery-prev${suffix}`}
              className="absolute left-3 md:left-5 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-[#FBE6D8]/85 hover:bg-[#FBE6D8] text-[#1F2D1B] flex items-center justify-center shadow-lg transition-colors"
            >
              <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
            </button>
            <button
              type="button"
              onClick={() => go(1)}
              aria-label="Next photo"
              data-testid={`gallery-next${suffix}`}
              className="absolute right-3 md:right-5 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-[#FBE6D8]/85 hover:bg-[#FBE6D8] text-[#1F2D1B] flex items-center justify-center shadow-lg transition-colors"
            >
              <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
            </button>
          </>
        )}

        {photos.length > 1 && (
          <div className="absolute bottom-4 md:bottom-6 right-5 md:right-8 flex items-center gap-2" data-testid={`gallery-dots${suffix}`}>
            {photos.map((p, i) => (
              <button
                key={p.id || i}
                type="button"
                onClick={() => setIdx(i)}
                aria-label={`Go to photo ${i + 1}`}
                data-testid={`gallery-dot${suffix}-${i}`}
                className="rounded-full transition-all shadow"
                style={{
                  width: i === idx ? 22 : 9,
                  height: 9,
                  backgroundColor: i === idx ? "#FBE6D8" : "rgba(251,230,216,0.5)",
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function HomeGallery({ items }) {
  const photos = (items && items.length ? items : DEFAULT_GALLERY).filter(
    (p) => p.enabled !== false && p.image
  );
  const [lightbox, setLightbox] = useState(null);
  const rowA = photos.filter((_, i) => i % 2 === 0);
  const rowB = photos.filter((_, i) => i % 2 === 1);

  useEffect(() => {
    if (lightbox === null) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowRight") setLightbox((v) => (v + 1) % photos.length);
      if (e.key === "ArrowLeft") setLightbox((v) => (v - 1 + photos.length) % photos.length);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightbox, photos.length]);

  if (!photos.length) return null;
  const openPhoto = (p) => setLightbox(photos.indexOf(p));

  return (
    <section
      data-testid="dahlias-gallery"
      className="bg-[var(--dahlias-cream)] text-[var(--dahlias-ink)]"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-28">
        <div className="text-center mb-10 md:mb-14">
          <span className="font-label tracking-[0.5em] text-[11px] text-[#6E1E3A] uppercase">
            ✦ From the Farm ✦
          </span>
          <h2 className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl text-[var(--dahlias-forest)] leading-[1.05]">
            The <span className="font-display-italic text-[#6E1E3A]">Gallery.</span>
          </h2>
        </div>

        <div className="space-y-6 md:space-y-8">
          <GalleryRow photos={rowA} row={1} frozen={lightbox !== null} onOpen={openPhoto} />
          {rowB.length > 0 && (
            <GalleryRow photos={rowB} row={2} reverse frozen={lightbox !== null} onOpen={openPhoto} />
          )}
        </div>
      </div>

      {/* Full-screen lightbox */}
      {lightbox !== null && photos[lightbox] && (
        <div
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4 md:p-10"
          onClick={() => setLightbox(null)}
        >
          <button
            type="button"
            aria-label="Close"
            data-testid="lightbox-close"
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 md:top-6 md:right-6 w-11 h-11 rounded-full bg-[#FBE6D8]/15 hover:bg-[#FBE6D8]/30 text-[#FBE6D8] flex items-center justify-center transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
          {photos.length > 1 && (
            <>
              <button
                type="button"
                aria-label="Previous photo"
                data-testid="lightbox-prev"
                onClick={(e) => { e.stopPropagation(); setLightbox((v) => (v - 1 + photos.length) % photos.length); }}
                className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-11 h-11 md:w-12 md:h-12 rounded-full bg-[#FBE6D8]/15 hover:bg-[#FBE6D8]/30 text-[#FBE6D8] flex items-center justify-center transition-colors"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                type="button"
                aria-label="Next photo"
                data-testid="lightbox-next"
                onClick={(e) => { e.stopPropagation(); setLightbox((v) => (v + 1) % photos.length); }}
                className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-11 h-11 md:w-12 md:h-12 rounded-full bg-[#FBE6D8]/15 hover:bg-[#FBE6D8]/30 text-[#FBE6D8] flex items-center justify-center transition-colors"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}
          <figure className="max-w-6xl max-h-full flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <img
              src={resolveAsset(photos[lightbox].image, "")}
              alt={photos[lightbox].caption || "Heritage Dahlias photo"}
              data-testid="lightbox-image"
              className="max-h-[80vh] max-w-full object-contain shadow-2xl"
            />
            <figcaption className="mt-4 text-center">
              {photos[lightbox].caption && (
                <span className="font-display-italic text-lg md:text-2xl text-[#FBE6D8]">
                  {photos[lightbox].caption}
                </span>
              )}
              <span className="block mt-1 font-label tracking-[0.3em] text-[10px] uppercase text-[#FBE6D8]/60">
                {lightbox + 1} / {photos.length}
              </span>
            </figcaption>
          </figure>
        </div>
      )}
    </section>
  );
}

/* ----------------------------------------------- BLOOM CLUB TEASER ------- */

function BloomClubTeaser() {
  return (
    <section
      data-testid="dahlias-bloom-club"
      className="relative overflow-hidden border-t-2 border-[#3E2346]/20"
      style={{ backgroundColor: "#E8DCEB" }}
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(ellipse at 20% 20%, rgba(255,255,255,0.5) 0%, transparent 55%), url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='220' height='220'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' seed='11'/><feColorMatrix values='0 0 0 0 0.62  0 0 0 0 0.50  0 0 0 0 0.67  0 0 0 0.12 0'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.55'/></svg>\")",
          backgroundSize: "auto, 240px 240px",
        }}
      />
      <div className="relative max-w-4xl mx-auto px-5 md:px-10 py-16 md:py-24 text-center">
        <div
          data-testid="dahlias-bloom-club-emblem"
          className="mx-auto inline-block"
        >
          <div className="font-label tracking-[0.45em] text-[10px] md:text-[11px] text-[#7B4F82] uppercase">
            ✦ Established 2026 ✦
          </div>
          <h2 className="mt-2 font-display uppercase text-4xl md:text-6xl text-[#3E2346] leading-[0.95] tracking-tight">
            The Heritage<br />
            <span className="font-display-italic normal-case text-[#7B4F82]">Bloom Club</span>
          </h2>
          <div className="mt-3 flex items-center justify-center gap-3 text-[#7B4F82]">
            <span aria-hidden="true" className="h-px w-10 bg-current opacity-50" />
            <span className="font-display-italic text-sm tracking-[0.05em]">
              An heirloom flower society
            </span>
            <span aria-hidden="true" className="h-px w-10 bg-current opacity-50" />
          </div>
        </div>
        <p className="mt-5 text-lg md:text-xl text-[#3E2346]/85 font-display italic max-w-2xl mx-auto leading-relaxed">
          Seasonal bloom boxes, hand picked by independent flower farms in your own region.
          Founded by Heritage Dahlias, EST 2026.
        </p>
        <a
          href="https://heritagebloomclub.nz"
          data-testid="bloom-club-cta"
          className="mt-9 inline-flex items-center gap-3 bg-[#3E2346] hover:bg-[#7B4F82] text-[#F2EAF3] px-9 py-4 font-display tracking-[0.2em] text-xs uppercase rounded-full shadow-[0_6px_24px_rgba(62,35,70,0.32)] transition-all hover:-translate-y-0.5"
        >
          Step inside <ArrowRight className="w-4 h-4" />
        </a>
      </div>
    </section>
  );
}

/* ----------------------------------------------- BREEDERS' BED TEASER ---- */

function BreedersBedTeaser() {
  return (
    <section
      data-testid="dahlias-breeders-bed"
      className="relative overflow-hidden border-t-2 border-[var(--berry)]/20"
      style={{ backgroundColor: "#F6EFE4" }}
    >
      <div className="relative max-w-4xl mx-auto px-5 md:px-10 py-16 md:py-24 text-center">
        <span className="inline-block font-display tracking-[0.5em] text-[11px] text-[var(--berry)] uppercase">
          ✦ Heritage Garden ✦
        </span>
        <h2 className="mt-5 font-display text-3xl md:text-5xl text-[var(--dahlias-ink)] leading-[1.05]">
          A bed for <span className="italic text-[var(--berry)]">breeders&rsquo; new dahlias.</span>
        </h2>
        <p className="mt-6 text-lg md:text-xl text-[var(--dahlias-ink)]/80 font-display italic max-w-2xl mx-auto leading-relaxed">
          Each summer the Heritage Garden plays host to dahlia seedlings bred by growers across
          Aotearoa. Submit yours and we&rsquo;ll send a NZ Post courier ticket plus credit toward our
          tubers and Bloom Boxes.
        </p>
        <Link
          to="/breeders-garden"
          data-testid="breeders-bed-cta"
          className="mt-9 inline-flex items-center gap-3 bg-[var(--dahlias-ink)] hover:bg-[var(--berry-deep)] text-[#FBE6D8] px-9 py-4 font-display tracking-[0.2em] text-xs uppercase rounded-full shadow-[0_6px_24px_rgba(110,30,58,0.2)] transition-all hover:-translate-y-0.5"
        >
          Submit your seedling <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </section>
  );
}

/* ======================================================================== */

export default function DahliasHome() {
  useDocumentTitle(
    "Heritage Dahlias · Heirloom dahlias hand-grown in Aotearoa since 1932",
    "Heritage Dahlias is a family-run flower farm in New Zealand. Tubers, mystery boxes, named heirloom varieties, and the Heritage Bloom Club seasonal subscription."
  );
  const introHeading = useSiteText(
    "dahlias.home.welcome.heading",
    "A small family farm,\n*growing dahlias the slow way.*"
  );
  const introBody = useSiteText(
    "dahlias.home.welcome.body",
    "Heritage Dahlias is a family-run flower farm in Aotearoa, growing exceptional dahlias by hand and carrying on the legacy our Nana Jean planted in 1932. A few rows. A little red shed. A lifetime of love for these flowers."
  );
  const heroImage = useSiteText("dahlias.home.hero", "");
  const heroLogo = useSiteText("dahlias.home.hero.logo", "");
  const heroSubtitle = useSiteText("dahlias.home.hero.subtitle", "Growing exceptional dahlias since 1932.");
  const announcementsRaw = useSiteText("dahlias.home.announcements", "");
  const galleryRaw = useSiteText("dahlias.home.gallery", "");
  const timelineRaw = useSiteText("dahlias.home.timeline", "");
  const announcements = useMemo(() => {
    try { const v = JSON.parse(announcementsRaw); return Array.isArray(v) ? v : null; } catch { return null; }
  }, [announcementsRaw]);
  const gallery = useMemo(() => {
    try { const v = JSON.parse(galleryRaw); return Array.isArray(v) ? v : null; } catch { return null; }
  }, [galleryRaw]);
  const timeline = useMemo(() => {
    try { const v = JSON.parse(timelineRaw); return Array.isArray(v) ? v : null; } catch { return null; }
  }, [timelineRaw]);

  const settings = useSiteSettings();

  const SECTIONS = {
    hero:          <HeritageHero key="hero" image={resolveAsset(heroImage, "")} subtitle={heroSubtitle} logo={resolveAsset(heroLogo, "")} />,
    announcements: <HomeAnnouncements key="announcements" items={announcements} />,
    gallery:       <HomeGallery key="gallery" items={gallery} />,
    seasons:       <Seasons key="seasons" />,
    introduction:  <Introduction key="introduction" heading={introHeading} body={introBody} />,
    "farm-life":   <LifeOnTheFarm key="farm-life" />,
    "bloom-club":  <BloomClubTeaser key="bloom-club" />,
    "breeders-bed": <BreedersBedTeaser key="breeders-bed" />,
    timeline:      <HeritageTimeline key="timeline" items={timeline} />,
  };
  const DEFAULT_ORDER = ["hero", "announcements", "gallery", "seasons", "introduction", "farm-life", "bloom-club", "breeders-bed", "timeline"];
  const config = Array.isArray(settings?.home_sections) && settings.home_sections.length
    ? settings.home_sections : DEFAULT_ORDER.map((slug) => ({ slug, visible: true }));
  const ordered = [
    ...config.filter((s) => SECTIONS[s.slug]),
    ...DEFAULT_ORDER.filter((slug) => !config.some((s) => s.slug === slug)).map((slug) => ({ slug, visible: true })),
  ];

  return (
    <DahliasShell>
      {ordered.filter((s) => s.visible !== false).map((s) => SECTIONS[s.slug])}
    </DahliasShell>
  );
}
